import { clockLabel } from './api/freeBusy'
import { payLinkLabel } from './payLinkLabel'
import type { MyHours } from './api/regularHours'
import type { TimeOffBlock } from './api/timeOff'

/** What the group holds, for when what is set cannot be read (D-209). */
export const HOURS_CONTENTS = 'Payment link, your hours and time off'

/**
 * The line under a closed *Privates & your hours* group (D-719).
 *
 * Collapsed by default so a teacher is not scrolling past nine boxes, which
 * only works if the closed group still answers "is this set up?" - so it names
 * what is set rather than what the group contains. Nothing set says so, because
 * a blank line reads as not loaded.
 *
 * **Not loaded is never "nothing set".** Every endpoint refuses for about 75
 * seconds after a deploy (D-33), and a teacher with hours reading *Nothing set
 * yet* would set them again. Until both lists arrive it names the contents.
 */
export function hoursSummary(
  paymentLinks: string[] | null | undefined,
  hours: MyHours | undefined,
  timeOff: TimeOffBlock[] | undefined,
  today: string,
): string {
  if (hours === undefined || timeOff === undefined) return HOURS_CONTENTS

  const parts: string[] = []

  // Named by service, e.g. "Venmo, Cash App links set" (D-725).
  if (paymentLinks && paymentLinks.length > 0) {
    const names = paymentLinks.map(payLinkLabel)
    parts.push(`${names.join(', ')} ${names.length === 1 ? 'link' : 'links'} set`)
  }

  const short = (day: string) => day.slice(0, 3)
  const inDays = [...new Set(hours.days.filter((d) => d.startTime !== null).map((d) => d.day))]
  const outDays = [...new Set(hours.days.filter((d) => d.startTime === null).map((d) => d.day))]

  // In and out counted apart: five days Out must not read as five days available.
  if (inDays.length === 1) {
    const stretches = hours.days.filter((d) => d.day === inDays[0])
    const first = stretches[0]

    parts.push(
      stretches.length === 1 && first.startTime && first.endTime
        ? `${short(first.day)} ${clockLabel(first.startTime)}–${clockLabel(first.endTime)}`
        : `${short(first.day)}, ${stretches.length} times`,
    )
  } else if (inDays.length > 1) {
    parts.push(`In ${inDays.length} days`)
  }

  if (outDays.length === 1) parts.push(`Out ${short(outDays[0])}`)
  else if (outDays.length > 1) parts.push(`Out ${outDays.length} days`)

  // Blocks not yet over; a finished holiday is history, not set-up.
  const coming = timeOff.filter((b) => b.to >= today).length
  if (coming > 0) parts.push(coming === 1 ? '1 time off coming up' : `${coming} times off coming up`)

  return parts.length > 0 ? parts.join(' · ') : 'Nothing set yet'
}
